import { isScreenshotMode } from "@constants/common";
import { isMockedData } from "@constants/demo";
import i18n from "@helpers/lib/i18n";
import { isWeb } from "@helpers/platform";
import { formatCount } from "@memorang/helpers";
import type {
	BundleMetaData,
	Distribution,
	MixedItemTag,
	Plan,
	TagsByType,
} from "@memorang/types";
import { sortedMasteryLevels } from "@memorang/ui/src/components/DonutChart/helpers";
import { updateUserProperties } from "analytics";
import { t } from "i18next";
import { DateTime } from "luxon";
import type { CurrentView } from "./types";

export const customTitleMap: Record<string, string> = {
	"Major Area": "Knowledge Levels",
	"Diagnostic Level": "Reasoning",
};

export const randomizeScore = () => {
	return Math.floor(Math.random() * 21) + 80;
};

const calculateTagDist = (tags: MixedItemTag[]): Distribution => {
	return tags.reduce(
		(acc, tag) => {
			Object.entries(tag.distribution).reduce((_, [key, value]) => {
				acc[key] += value;
				return acc;
			}, acc);
			return acc;
		},
		{
			...Object.assign(
				sortedMasteryLevels.reduce<Distribution>((acc, level) => {
					acc[level.id] = 0;
					return acc;
				}, {}),
				{ none: 0 },
			),
		},
	);
};
const getItemsCountDescription = (count: number) => {
	const questions = count;
	// const flashcards = items.filter((item) => item.type === 'flashcard').length;
	const description = [
		...(questions
			? [`${questions} ${formatCount("question", questions)}`]
			: []),
		// ...(flashcards ? [`${flashcards} flashcards`] : []),
	].join(" • ");

	return description;
};
const filterTagsByType = (tagsByType: TagsByType, currentView: CurrentView) => {
	return Object.keys(tagsByType).reduce((a: TagsByType, c) => {
		const currentTags = tagsByType[c];
		const currentTagsWithDist = currentTags.filter(
			(item: MixedItemTag) =>
				item.displayType?.includes(currentView) && (item.numItems || 0) > 0,
		);
		a[c] = currentTagsWithDist;
		return a;
	}, {});
};
const groupTagsByType = (
	tags: MixedItemTag[],
	currentView: CurrentView,
	freeTagIds: string[],
	hasAccess?: boolean,
	includeCount?: boolean,
) => {
	const tagsByType = tags.reduce((acc: TagsByType, tag) => {
		const tagType = customTitleMap[tag.type] || tag.type;
		if (!acc[tagType]) {
			acc[tagType] = [];
		}

		const dist = tag.distribution;

		const newTag = {
			...tag,
			locked: hasAccess ? false : !freeTagIds?.includes(tag.id),
			distribution: dist,
			description: getItemsCountDescription(tag.numItems || 0),
			count: tag.numItems || 0,
			type: "Skill",
			title: i18n.exists(`dashboard.skills.${tag.title || tag.label}`)
				? t(`dashboard.skills.${tag.title || tag.label}`)
				: tag.title || tag.label || "",
			// score: (
			// 	(dist.high / (dist.medium + dist.low + dist.none + dist.high)) *
			// 	100
			// ).toFixed(),
			score: Math.round(tag.averageScore || 0),
			...(includeCount
				? {
						count: tag.numItems,
					}
				: {}),
		};
		acc[tagType].push(newTag);
		return acc;
	}, {});

	const filteredTagsByType = filterTagsByType(tagsByType, currentView);

	return filteredTagsByType;
};

const hasProductAccess = (
	productMetadata?: BundleMetaData,
	productUuid?: string,
) => {
	if (productMetadata) {
		const {
			subscription: { status, endDate },
		} = productMetadata;
		const isSubExpired = endDate
			? DateTime.now() > DateTime.fromISO(endDate)
			: true;
		const hasAccess = status === "ACTIVE" && !isSubExpired;
		if (productUuid && hasAccess) {
			updateUserProperties({
				premium_user: "true",
				products: JSON.stringify([
					{
						id: productUuid,
						type: "study_pack",
					},
				]),
			});
		}
		return isScreenshotMode || isMockedData ? true : hasAccess;
	}
	return false;
};

const filterPlans = (plans: Plan[]) => {
	return plans.filter(
		(plan) =>
			(isWeb && plan.isMobile === false) ||
			(!isWeb && plan.isMobile === true) ||
			plan.isMobile == null,
	);
};
export { groupTagsByType, hasProductAccess, calculateTagDist, filterPlans };
